import React, { useState, useEffect } from "react";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import Button from "../../components/ui/Button";
import FilterOptions from "./components/FilterOptions";
import ContactCTA from "./components/ContactCTA";
import Icon from "../../components/AppIcon";

import { motion, AnimatePresence } from 'framer-motion';
import { Code, Smartphone, Cloud, GitBranch, Shield, BarChart, Briefcase, Palette, HardDrive, Cpu, Brain, Link, ChevronDown, Sparkles, ArrowRight } from 'lucide-react';

const iconMap = {
  Code, Smartphone, Cloud, GitBranch, Shield, BarChart,
  Briefcase, Palette, HardDrive, Cpu, Brain, Link
};

// ===== SERVICE CARD COMPONENT =====
const ServiceCard = ({ service, isExpanded, onExpand }) => {
  const [isHovered, setIsHovered] = useState(false);
  const IconComponent = iconMap[service.icon];

  return (
    <div
      className="relative h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.div
        className="relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 h-full flex flex-col"
        whileHover={{ y: -12 }}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
      >
        {/* Image Section with Enhanced Overlay */}
        <div className="relative h-56 3xl:h-64 overflow-hidden">
          <motion.img
            src={service.image}
            alt={service.name}
            className="w-full h-full object-cover"
            animate={{ scale: isHovered ? 1.15 : 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />

          {/* Multi-layer Gradient Overlay */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-br from-blue-900/70 via-purple-900/50 to-transparent"
            animate={{ opacity: isHovered ? 1 : 0.8 }}
            transition={{ duration: 0.4 }}
          />

          {/* Animated Icon Badge */}
          <motion.div
            className="absolute top-5 right-5 bg-white/95 backdrop-blur-md p-3.5 3xl:p-4 rounded-2xl shadow-xl"
            animate={{
              y: isHovered ? -6 : 0,
              rotate: isHovered ? 8 : 0,
              scale: isHovered ? 1.1 : 1
            }}
            transition={{ type: "spring", stiffness: 400, damping: 15 }}
          >
            <IconComponent className="w-7 h-7 3xl:w-8 3xl:h-8 text-blue-600" strokeWidth={2.5} />
          </motion.div>

          {/* Enhanced Category Badge */}
          <motion.div
            className="absolute top-5 left-5 px-4 py-2 3xl:px-5 3xl:py-2.5 rounded-full text-xs 3xl:text-sm 4xl:text-base 5xl:text-lg 6xl:text-xl font-bold uppercase tracking-wider backdrop-blur-md bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg"
            whileHover={{ scale: 1.05 }}
          >
            {service.category}
          </motion.div>

          {/* Sparkle Effect on Hover */}
          <AnimatePresence>
            {isHovered && (
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                className="absolute bottom-5 right-5"
              >
                <Sparkles className="w-6 h-6 3xl:w-8 3xl:h-8 text-yellow-300" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Content Section with Better Typography */}
        <div className="p-7 3xl:p-8 flex-grow flex flex-col">
          {/* Title with Better Typography */}
          <h3
            className="text-2xl 3xl:text-3xl 4xl:text-4xl 5xl:text-5xl 6xl:text-6xl font-bold text-gray-900 mb-3 leading-tight tracking-tight"
            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
          >
            {service.name}
          </h3>

          {/* Description with Improved Readability */}
          <p
            className="text-gray-600 mb-6 leading-relaxed text-[15px] 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl"
            style={{ lineHeight: '1.7' }}
          >
            {service.description}
          </p>

          {/* Benefits Section with Better Visual Hierarchy */}
          <div className="mb-6">
            <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center uppercase tracking-wide">
              <motion.span
                className="w-1.5 h-5 bg-gradient-to-b from-blue-600 to-blue-700 rounded-full mr-2.5"
                animate={{ scaleY: isHovered ? 1.2 : 1 }}
                transition={{ duration: 0.3 }}
              />
              Key Benefits
            </h4>
            <div className="space-y-3">
              {service.benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start group"
                >
                  <motion.div
                    className="flex-shrink-0 w-6 h-6 rounded-xl bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center mt-0.5 mr-3.5 shadow-sm"
                    whileHover={{ scale: 1.3, rotate: 360 }}
                    transition={{ duration: 0.5, type: "spring" }}
                  >
                    <svg className="w-3.5 h-3.5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </motion.div>
                  <span className="text-[15px] 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl text-gray-700 leading-relaxed group-hover:text-gray-900 transition-colors">
                    {benefit}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Expandable Details with Better Typography */}
          <AnimatePresence initial={false}>
            {isExpanded && (
              <motion.div
                key="expanded-content"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden mb-6"
              >
                <div className="pt-5 border-t border-gray-100">
                  <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center uppercase tracking-wide">
                    <span className="w-1.5 h-5 bg-gradient-to-b from-purple-600 to-purple-700 rounded-full mr-2.5" />
                    Detailed Overview
                  </h4>
                  <div className="space-y-4 text-[15px] 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl text-gray-600 leading-relaxed" style={{ lineHeight: '1.8' }}>
                    {service.details.split('\n\n').map((paragraph, index) => (
                      <p
                        key={index}
                        className="text-justify"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Enhanced Action Button */}
          {/* <button
            type="button"
            onClick={onExpand}
            className="mt-auto w-full bg-gradient-to-r from-blue-600 via-blue-700 to-blue-600 text-white py-3.5 px-6 rounded-2xl font-semibold text-[15px] flex items-center justify-center gap-2.5 overflow-hidden relative group shadow-md hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
            style={{ backgroundSize: '200% 100%' }}
          >
            <span className="relative z-10 tracking-wide">
              {isExpanded ? "Show Less" : "Learn More"}
            </span>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.4, type: "spring" }}
              className="relative z-10"
            >
              <ChevronDown className="w-5 h-5" strokeWidth={2.5} />
            </motion.div>
          </button> */}
        </div>

        {/* Enhanced Decorative Blur Elements */}
        <motion.div
          className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl pointer-events-none"
          animate={{
            scale: isHovered ? 1.8 : 1,
            opacity: isHovered ? 0.4 : 0.15,
          }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
        <motion.div
          className="absolute -bottom-24 -left-24 w-48 h-48 bg-gradient-to-tr from-purple-400/20 to-pink-400/20 rounded-full blur-3xl pointer-events-none"
          animate={{
            scale: isHovered ? 1.8 : 1,
            opacity: isHovered ? 0.4 : 0.15,
          }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
        />
      </motion.div>
    </div>
  );
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(6);
  const [expandedCardId, setExpandedCardId] = useState(null);

  // Mock data for services
  const serviceData = [
    // ===== NEW DEVELOPMENT SERVICES (4) =====
    {
      id: 1,
      name: "AI Automation",
      category: "development",
      icon: "Brain",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Make your business work smarter with intelligent automation. We setup AI workflows that reduce manual work, eliminate mistakes, and increase speed.",
      benefits: [
        "Lead follow-up automation",
        "Automated email replies",
        "Customer support automation",
        "Data entry elimination"
      ],
      details: `Our AI automation services transform manual, repetitive tasks into intelligent workflows that work 24/7. We implement cutting-edge AI solutions that understand context, make decisions, and execute actions without human intervention.

What we automate: Lead follow-up, email replies, customer support, data entry, invoice processing, WhatsApp automation, workflow triggers, and document extraction. Our solutions integrate seamlessly with your existing systems.

Tools Used: We leverage OpenAI for natural language processing, AWS for scalable infrastructure, Make and Zapier for workflow orchestration, and custom APIs for specialized integrations. Our automation solutions save businesses an average of 20-30 hours per week while improving accuracy and response times.`
    },
    {
      id: 2,
      name: "Custom Internal Tools",
      category: "development",
      icon: "Code",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Tailored systems built exactly around your business needs. We develop custom software that helps you replace Excel sheets and manual processes.",
      benefits: [
        "Replace spreadsheets with robust systems",
        "Custom workflow automation",
        "Role-based access control",
        "Real-time reporting and analytics"
      ],
      details: `We build custom internal tools that perfectly match your unique business processes. No more forcing your operations to fit generic software - we create solutions that adapt to you.

Examples: CRM systems, inventory & purchase management, HRMS & attendance tracking, finance & expense tracking, order & dispatch systems, appointment/clinic management, and distributor/retail workflow solutions.

Our custom tools eliminate the need for multiple disconnected software subscriptions. We design intuitive interfaces that your team can start using immediately, with comprehensive training and documentation. Every tool is built with scalability in mind, growing alongside your business needs.`
    },
    {
      id: 3,
      name: "Web Application Development",
      category: "development",
      icon: "Code",
      image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Fast, modern and scalable web applications. We build fully responsive and scalable web applications using the latest technologies.",
      benefits: [
        "Mobile-responsive design",
        "Lightning-fast performance",
        "Secure and scalable architecture",
        "Modern user interface"
      ],
      details: `Our web application development services deliver high-performance, scalable solutions built with modern technologies. Every application we build is designed to handle growth, from startup MVPs to enterprise-scale platforms.

Stack: React.js for dynamic frontends, Node.js for robust backends, MongoDB and MySQL for flexible data storage, and AWS Cloud for reliable hosting and scaling.

Solutions we build: Corporate websites, client portals, admin dashboards, booking systems, SaaS applications, and custom user platforms. We follow agile development practices with continuous client collaboration, ensuring the final product exceeds expectations while staying on schedule and budget.`
    },
    {
      id: 4,
      name: "Cloud Integration Services",
      category: "development",
      icon: "Link",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Connect your tools. Automate your operations. We integrate any platform with your workflows to unify your systems.",
      benefits: [
        "Unified system communication",
        "Real-time data synchronization",
        "Reduced manual data entry",
        "Seamless platform connectivity"
      ],
      details: `Our cloud integration services eliminate data silos by connecting all your business tools into one cohesive ecosystem. We create seamless data flows between platforms, ensuring information is always up-to-date across your entire tech stack.

Integrations: WhatsApp Business API, Tally and Zoho Books accounting sync, payment gateways (Razorpay, Stripe, PayPal), SMS/Email automation platforms, CRM connections, and Google Sheets/API synchronization.

We handle complex integration challenges including authentication, data transformation, error handling, and monitoring. Whether you need simple two-way sync or complex multi-platform workflows, our integration solutions ensure your systems work together harmoniously, reducing manual work and eliminating data inconsistencies.`
    },

    // ===== EXISTING DEVELOPMENT SERVICES (4) =====
    {
      id: 5,
      name: "Mobile App Development",
      category: "development",
      icon: "Smartphone",
      image: "https://images.pexels.com/photos/1092644/pexels-photo-1092644.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "Native and cross-platform mobile applications for iOS and Android. We create intuitive, high-performance apps that deliver exceptional user experiences.",
      benefits: [
        "Native and cross-platform options",
        "Intuitive user interfaces",
        "Offline functionality",
        "App store optimization"
      ],
      details: `Our mobile app development services cover both native (iOS and Android) and cross-platform solutions using frameworks like React Native and Flutter. We focus on creating apps that not only look great but also perform exceptionally well.

From concept to launch, we handle every aspect of the mobile app development process, including market research, UX/UI design, development, testing, and deployment to app stores. We also provide ongoing maintenance and updates to ensure your app remains competitive and functional.

Our mobile apps are built with features like offline functionality, push notifications, secure authentication, and seamless API integrations to provide a comprehensive solution for your business needs.`
    },
    {
      id: 6,
      name: "IoT Solutions",
      category: "development",
      icon: "Cpu",
      image: "https://images.pexels.com/photos/373543/pexels-photo-373543.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "Connect devices and gather valuable data with our Internet of Things solutions. We design and implement IoT systems for various industries and use cases.",
      benefits: [
        "Real-time data collection",
        "Remote monitoring capabilities",
        "Automated processes",
        "Data-driven insights"
      ],
      details: `Our IoT solutions connect devices, systems, and data to create intelligent networks that provide valuable insights and enable automation. We design and implement end-to-end IoT systems tailored to your specific industry and use cases.

Our IoT services include sensor and device selection, connectivity solutions, IoT platform development, data collection and analysis, dashboard creation, and integration with existing business systems.

We have experience implementing IoT solutions across various industries including manufacturing, healthcare, agriculture, retail, and smart buildings. Our solutions help organizations improve operational efficiency, enable predictive maintenance, enhance customer experiences, and create new business models based on connected devices and data.`
    },
    {
      id: 7,
      name: "Blockchain Development",
      category: "development",
      icon: "Link",
      image: "https://images.pexels.com/photos/8370752/pexels-photo-8370752.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "Implement secure, transparent, and efficient blockchain solutions for your business. We develop smart contracts, decentralized applications, and private blockchain networks.",
      benefits: [
        "Enhanced security",
        "Transparent transactions",
        "Reduced intermediaries",
        "Immutable record-keeping"
      ],
      details: `Our blockchain development services help organizations leverage distributed ledger technology to create secure, transparent, and efficient solutions. We work with various blockchain platforms including Ethereum, Hyperledger, and Binance Smart Chain to develop custom blockchain applications.

Our blockchain services include smart contract development, decentralized application (dApp) creation, tokenization, private blockchain network implementation, and integration of blockchain with existing systems.

We have experience applying blockchain technology across various use cases including supply chain tracking, digital identity verification, secure document management, tokenization of assets, and creating transparent audit trails. Our solutions help organizations reduce fraud, increase trust, and create new business models based on blockchain technology.`
    },
    {
      id: 8,
      name: "E-commerce Solutions",
      category: "development",
      icon: "Smartphone",
      image: "https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Complete e-commerce platforms with payment integration, inventory management, and seamless shopping experiences across all devices.",
      benefits: [
        "Secure payment processing",
        "Inventory management",
        "Mobile-optimized checkout",
        "Multi-vendor support"
      ],
      details: `Our e-commerce solutions provide end-to-end platforms for selling products and services online. We build custom e-commerce systems tailored to your business model, whether B2C, B2B, marketplace, or subscription-based.

We integrate leading payment gateways, implement robust inventory management, create intuitive product catalogs, and optimize the entire customer journey from browsing to checkout. Our solutions support multiple currencies, languages, and shipping options.

Features include abandoned cart recovery, customer accounts, wishlists, product recommendations, analytics dashboards, and marketing integrations. We ensure your e-commerce platform is fast, secure, and designed to maximize conversions while providing an excellent shopping experience.`
    },

    // ===== OTHER CATEGORIES =====
    {
      id: 9,
      name: "Cloud Solutions",
      category: "infrastructure",
      icon: "Cloud",
      image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      description: "Comprehensive cloud infrastructure and migration services. We help businesses leverage the power of AWS, Azure, and Google Cloud for scalability and reliability.",
      benefits: [
        "Cost-effective scaling",
        "Enhanced security",
        "Disaster recovery",
        "24/7 availability"
      ],
      details: `Our cloud solutions help businesses of all sizes leverage the power of cloud computing to improve scalability, reduce costs, and enhance security. We provide services for AWS, Microsoft Azure, and Google Cloud Platform.

Our cloud services include infrastructure setup, migration from on-premises to cloud, hybrid cloud solutions, cloud-native application development, and ongoing management and optimization of cloud resources.

We implement best practices for cloud security, cost management, and performance optimization to ensure your cloud infrastructure is reliable, secure, and cost-effective. Our team also provides training and support to help your staff make the most of your cloud investment.`
    },
    {
      id: 10,
      name: "DevOps Services",
      category: "infrastructure",
      icon: "GitBranch",
      image: "https://images.pexels.com/photos/1181467/pexels-photo-1181467.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "Streamline your development and operations with our DevOps services. We implement CI/CD pipelines, infrastructure as code, and automated testing.",
      benefits: [
        "Faster time to market",
        "Improved collaboration",
        "Automated workflows",
        "Reduced deployment failures"
      ],
      details: `Our DevOps services help organizations streamline their software development and IT operations to deliver applications and services at high velocity. We implement practices, tools, and cultural philosophies that improve your ability to deliver applications and services quickly and reliably.

We set up continuous integration and continuous delivery (CI/CD) pipelines, implement infrastructure as code using tools like Terraform and Ansible, and establish monitoring and logging solutions to provide visibility into application and infrastructure performance.

Our DevOps approach emphasizes collaboration between development and operations teams, automation of manual tasks, and measurement of application performance to continuously improve your software delivery process.`
    },
    {
      id: 11,
      name: "Cybersecurity",
      category: "security",
      icon: "Shield",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      description: "Protect your business with our comprehensive cybersecurity services. We offer vulnerability assessments, penetration testing, and security strategy development.",
      benefits: [
        "Threat detection and prevention",
        "Regulatory compliance",
        "Data protection",
        "Security awareness training"
      ],
      details: `Our cybersecurity services provide comprehensive protection for your digital assets against evolving cyber threats. We take a proactive approach to security, identifying and addressing vulnerabilities before they can be exploited.

Our services include security assessments, penetration testing, security architecture design, implementation of security controls, incident response planning, and security awareness training for your employees.

We help organizations comply with industry regulations and standards such as GDPR, HIPAA, PCI DSS, and ISO 27001, ensuring that your security measures meet or exceed required standards while protecting sensitive data and maintaining customer trust.`
    },
    {
      id: 12,
      name: "Data Analytics",
      category: "data",
      icon: "BarChart",
      image: "https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "Transform your data into actionable insights with our analytics services. We design and implement data warehouses, BI solutions, and predictive models.",
      benefits: [
        "Data-driven decision making",
        "Business intelligence dashboards",
        "Predictive analytics",
        "Custom reporting solutions"
      ],
      details: `Our data analytics services help organizations harness the power of their data to gain valuable insights and make informed business decisions. We work with structured and unstructured data to uncover patterns, correlations, and trends that drive business value.

We design and implement data warehouses, data lakes, and business intelligence solutions that consolidate data from multiple sources and make it accessible for analysis. Our team develops custom dashboards and reports that present complex data in an intuitive, visual format.

Using advanced analytics techniques including machine learning and predictive modeling, we help you move beyond descriptive analytics to predictive and prescriptive insights that can guide future strategy and operations.`
    },
    {
      id: 13,
      name: "Artificial Intelligence",
      category: "data",
      icon: "Brain",
      image: "https://images.unsplash.com/photo-1507146153580-69a1fe6d8aa1?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      description: "Leverage the power of AI to automate processes and gain insights. We develop machine learning models, natural language processing systems, and computer vision solutions.",
      benefits: [
        "Process automation",
        "Advanced data analysis",
        "Personalized user experiences",
        "Predictive capabilities"
      ],
      details: `Our artificial intelligence services help organizations harness the power of AI to solve complex problems, automate processes, and gain deeper insights from their data. We develop custom AI solutions tailored to your specific business needs and objectives.

Our AI capabilities include machine learning for predictive analytics and pattern recognition, natural language processing for text analysis and conversational interfaces, computer vision for image and video analysis, and recommendation systems for personalized experiences.

We follow a practical approach to AI implementation, focusing on use cases that deliver tangible business value. Our process includes data assessment, algorithm selection and development, model training and validation, deployment, and ongoing monitoring and improvement of AI systems.`
    },
    {
      id: 14,
      name: "IT Consulting",
      category: "consulting",
      icon: "Briefcase",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      description: "Strategic IT consulting to align technology with your business goals. We provide roadmapping, technology selection, and digital transformation guidance.",
      benefits: [
        "Technology roadmapping",
        "Digital transformation strategy",
        "Vendor selection assistance",
        "IT governance frameworks"
      ],
      details: `Our IT consulting services provide strategic guidance to help organizations leverage technology effectively to achieve their business objectives. We work with you to understand your business challenges and goals, then develop technology strategies that address your specific needs.

Our consultants bring deep expertise in various technology domains and industries, allowing us to provide informed recommendations on technology selection, architecture design, and implementation approaches.

We assist with digital transformation initiatives, helping organizations modernize legacy systems, adopt cloud technologies, implement data-driven decision making, and create more efficient business processes through technology.`
    },
    {
      id: 15,
      name: "UI/UX Design",
      category: "design",
      icon: "Palette",
      image: "https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "Create exceptional user experiences with our UI/UX design services. We focus on user research, information architecture, and intuitive interface design.",
      benefits: [
        "User-centered design approach",
        "Improved user satisfaction",
        "Increased conversion rates",
        "Consistent brand experience"
      ],
      details: `Our UI/UX design services focus on creating digital experiences that are both visually appealing and highly functional. We follow a user-centered design process that begins with understanding your users' needs, goals, and pain points.

Our design process includes user research, persona development, information architecture, wireframing, prototyping, and visual design. We create intuitive navigation structures, clear visual hierarchies, and engaging interactions that guide users toward their goals.

We conduct usability testing throughout the design process to validate our design decisions and ensure the final product meets user expectations. Our designs are responsive, accessible, and aligned with your brand identity to create a cohesive user experience across all touchpoints.`
    },
    {
      id: 16,
      name: "Managed IT Services",
      category: "support",
      icon: "HardDrive",
      image: "https://images.unsplash.com/photo-1551033406-611cf9a28f67?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      description: "Comprehensive IT management and support for your business. We provide proactive monitoring, maintenance, and help desk services to keep your systems running smoothly.",
      benefits: [
        "Proactive system monitoring",
        "Reduced downtime",
        "Predictable IT costs",
        "Expert technical support"
      ],
      details: `Our managed IT services provide comprehensive support for your entire IT infrastructure, allowing you to focus on your core business while we handle your technology needs. We offer proactive monitoring, maintenance, and support to prevent issues before they impact your business.

Our services include 24/7 system monitoring, regular maintenance and updates, help desk support, network management, security monitoring, backup and disaster recovery, and strategic IT planning.

With our managed services, you benefit from predictable IT costs, reduced downtime, improved security, and access to a team of IT experts without the expense of maintaining a large in-house IT department. We become your technology partner, aligning IT operations with your business objectives.`
    }
  ];

  const categories = [
    { id: "all", name: "All Services" },
    { id: "development", name: "Development" },
    { id: "infrastructure", name: "Infrastructure" },
    { id: "security", name: "Security" },
    { id: "data", name: "Data & AI" },
    { id: "consulting", name: "Consulting" },
    { id: "design", name: "Design" },
    { id: "support", name: "Support" }
  ];

  // Simulate loading data
  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      setServices(serviceData);
      setFilteredServices(serviceData);
      setIsLoading(false);
    }, 800);
  }, []);

  // Filter services based on selected category
  useEffect(() => {
    if (selectedCategory === "all") {
      setFilteredServices(services);
    } else {
      setFilteredServices(services.filter(service => service.category === selectedCategory));
    }
    setVisibleCount(6);
    setExpandedCardId(null); // Reset expanded card when category changes
  }, [selectedCategory, services]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const loadMore = () => {
    setVisibleCount(prevCount => prevCount + 6);
  };

  const handleCardExpand = (serviceId) => {
    setExpandedCardId(expandedCardId === serviceId ? null : serviceId);
  };

  const hasMore = visibleCount < filteredServices.length;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50/30 mt-10 overflow-x-hidden">
      <Header />

      <main id="main-content" className="flex-grow">
        {/* Enhanced Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 text-white py-20 3xl:py-32 overflow-hidden">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          </div>

          {/* REMOVED padding from container */}
          <div className="container mx-auto relative z-10 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl 3xl:max-w-5xl 4xl:max-w-6xl mx-auto text-center px-4 sm:px-6 lg:px-8"
            >
              {/* Animated Badge */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-5 py-2.5 3xl:px-6 3xl:py-3 rounded-full mb-6 3xl:mb-8 border border-white/20"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                >
                  <Sparkles className="w-5 h-5 3xl:w-6 3xl:h-6 text-yellow-300" />
                </motion.div>
                <span className="text-sm 3xl:text-base font-bold uppercase tracking-wider">Enterprise Solutions</span>
              </motion.div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl 3xl:text-7xl font-bold mb-6 3xl:mb-8 leading-tight tracking-tight">
                Our IT Services
              </h1>
              <p className="text-lg md:text-xl 3xl:text-2xl 4xl:text-3xl 5xl:text-4xl 6xl:text-5xl opacity-95 leading-relaxed max-w-3xl 3xl:max-w-4xl 4xl:max-w-5xl mx-auto" style={{ lineHeight: '1.8' }}>
                We deliver smart scalable technology solutions tailored to your business needs. From AI automation to modern web development, our expert team helps you streamline operations, enhance efficiency, and achieve sustainable growth.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Enhanced Filter Section */}
        <section className="py-8 bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-40 shadow-sm">
          {/* REMOVED padding from container, add to inner div */}
          <div className="container mx-auto 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
            <div className="px-4 sm:px-6 lg:px-8">
              <FilterOptions
                categories={categories}
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange}
              />
            </div>
          </div>
        </section>

        {/* Services Grid with Responsive Layout */}
        <section className="py-16 md:py-20 3xl:py-32 bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20">
          {/* REMOVED padding from container, add to inner div */}
          <div className="container mx-auto 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
            <div className="px-4 sm:px-6 lg:px-8">
              {isLoading ? (
                <div className="flex flex-col justify-center items-center py-24">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full"
                  />
                  <p className="mt-6 text-gray-600 font-medium">Loading services...</p>
                </div>
              ) : (
                <>
                  {/* Grid with Enhanced ServiceCard Components */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 xl:gap-12 3xl:gap-14 4xl:gap-16">
                    {filteredServices.slice(0, visibleCount).map((service, index) => (
                      <motion.div
                        key={service.id}
                        initial={{ opacity: 0, y: 60 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: index * 0.1,
                          duration: 0.6,
                          ease: [0.22, 1, 0.36, 1]
                        }}
                      >
                        <ServiceCard
                          service={service}
                          isExpanded={expandedCardId === service.id}
                          onExpand={() => handleCardExpand(service.id)}
                        />
                      </motion.div>
                    ))}
                  </div>

                  {/* Enhanced Load More Button */}
                  {hasMore && (
                    <motion.div
                      className="mt-16 text-center"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <Button
                        size="lg"
                        icon="ArrowRight"
                        iconPosition="right"
                        onClick={loadMore}
                      >
                        Load More Services
                      </Button>
                    </motion.div>
                  )}

                  {/* Enhanced No Results Message */}
                  {filteredServices.length === 0 && (
                    <motion.div
                      className="text-center py-20"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Icon name="Search" size={56} className="mx-auto text-slate-300 mb-6" />
                      <h3 className="text-2xl font-bold text-slate-700 mb-3">No services found</h3>
                      <p className="text-slate-500 mb-8 text-lg">We couldn't find any services matching your criteria.</p>
                      <Button
                        variant="primary"
                        onClick={() => setSelectedCategory("all")}
                        className="shadow-lg"
                      >
                        View All Services
                      </Button>
                    </motion.div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        {/* Contact CTA Section */}
        <ContactCTA />
      </main>

      <Footer />
    </div>
  );



};

export default ServicesPage;
